/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import { MailIcon } from '@heroicons/react/solid'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Button, Form as NXUIForm } from '@nexigendigital/nxui'
import { useRecoverPasswordMutation } from 'api/app'

/**********************************************************************************************************
 *   STYLE IMPORTS
 **********************************************************************************************************/
import { Recovery } from './form.style'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function ForgotForm() {
    const passwordRef = useRef()
    const navigate = useNavigate()
    const [recipientEmail, setRecipientEmail] = useState('text@example.com')
    const [forgotTemplate, setForgotTemplate] = useState('form')
    const [recoverPassword, { isSuccess, isError }] = useRecoverPasswordMutation()
    const { appSettings: { provider: { email_support } } } = useSelector((state) => state.app)

    function redirectToLogin() {
        navigate('/login', { replace: true })
    }

    useEffect(() => {
        if (isSuccess) {
            setForgotTemplate('success')
        } else if (isError) {
            passwordRef.current.setSubmitting(false)
        }
    }, [isSuccess, isError])

    const PasswordResetForm = () => {
        return (
            <>
                <Recovery.Default.Header.Container>
                    <Recovery.Default.Header.Title>Password Recovery</Recovery.Default.Header.Title>
                    <Recovery.Default.Header.Description>Enter your email address below to begin the password recovery process.</Recovery.Default.Header.Description>
                </Recovery.Default.Header.Container>
                <Formik
                    innerRef={passwordRef}
                    initialValues={{
                        email: ''
                    }}
                    validationSchema={Yup.object({
                        email: Yup.string().email('Invalid email address').required('Required')
                    })}
                    onSubmit={({ email }) => {
                        setRecipientEmail(email)
                        recoverPassword({ email })
                    }}
                >
                    {({ isSubmitting }) => {
                        return (
                            <Form>
                                <Recovery.Default.Container>
                                    <Recovery.Form.Input
                                        label={'Email address'}
                                        name={'email'}
                                        type={'email'}
                                        disabled={isSubmitting}
                                        component={NXUIForm.InputField}
                                    />
                                </Recovery.Default.Container>
                                <Recovery.Footer>
                                    <Button type={'button'} color={'secondary'} onClick={redirectToLogin} disabled={isSubmitting}>Back</Button>
                                    <Button
                                        color={'primary'}
                                        type={'submit'}
                                        disabled={isSubmitting}
                                        loading={isSubmitting ? 'Submitting' : false}
                                    >
                                        Submit
                                    </Button>
                                </Recovery.Footer>
                            </Form>
                        )
                    }}
                </Formik>
            </>
        )
    }

    const PasswordResetSuccess = () => {
        return (
            <>
                <Recovery.CTA.Header.Container>
                    <Recovery.CTA.Header.Icon><MailIcon width={'24px'} aria-hidden="true" /></Recovery.CTA.Header.Icon>
                    <Recovery.CTA.Header.Title>Password Reset Link Sent</Recovery.CTA.Header.Title>
                    <Recovery.CTA.Header.Description>A password reset link has been sent to <strong>{recipientEmail}</strong>.</Recovery.CTA.Header.Description>
                </Recovery.CTA.Header.Container>
                <Recovery.CTA.Container>
                    <Recovery.Button
                        color={'primary'}
                        type={'button'}
                        onClick={redirectToLogin}
                    >
                        Return to Login
                    </Recovery.Button>
                </Recovery.CTA.Container>
                {/* <Recovery.CTA.Footer>
                    <Recovery.CTA.Anchor
                        color={'primary'}
                        type={'button'}
                        onClick={() => setForgotTemplate('enquiry')}
                    >
                        Didn&apos;t receive an email?
                    </Recovery.CTA.Anchor>
            </Recovery.CTA.Footer> */}
            </>
        )
    }

    const Enquiry = () => {
        return (
            <>
                <Recovery.CTA.Header.Container>
                    <Recovery.CTA.Header.Title>Didn&apos;t receive an email?</Recovery.CTA.Header.Title>
                    <Recovery.CTA.Header.Description>
                        Try checking your spam and junk folders.
                        To ensure you receive emails to the correct inbox,
                        please whitelist <strong>{email_support}</strong>
                    </Recovery.CTA.Header.Description>
                </Recovery.CTA.Header.Container>
                <Recovery.CTA.Container>
                    <Recovery.Button
                        color={'secondary'}
                        type={'button'}
                        onClick={() => setForgotTemplate('form')}
                    >
                        Resend Password Reset Email
                    </Recovery.Button>
                </Recovery.CTA.Container>
                <Recovery.CTA.Footer>
                    <Recovery.CTA.Anchor
                        color={'primary'}
                        type={'button'}
                        onClick={redirectToLogin}
                    >
                        Return to Login
                    </Recovery.CTA.Anchor>
                </Recovery.CTA.Footer>
            </>
        )
    }

    const Template = () => {
        switch (forgotTemplate) {
            case 'success':
                return PasswordResetSuccess()
            case 'enquiry':
                return Enquiry()
            default:
                return PasswordResetForm()
        }
    }

    return Template()
}
